import type { OrganizationRef } from '@dashboard/types/organization'
import type { TeamListItem } from './teamStore'

export interface ProjectOptionGroup<T extends TeamListItem = TeamListItem> {
  key: string
  /** Null for the single unlabeled group shown when no project names an organization. */
  name: string | null
  organization: OrganizationRef | null
  /** Agency names the user reaches this organization's projects through. */
  via: string[]
  /** Set when the user belongs to the organization, so the header can open its page. */
  memberOrganizationPid: string | null
  projects: T[]
}

/**
 * Projects grouped by the organization that owns them. Organizations the user
 * belongs to come first, then the ones they reach through an agency link or a
 * direct grant, each in the order `GET /api/teams` lists them.
 */
export function groupProjects<T extends TeamListItem>(
  teams: T[],
  memberOrganizationPids: string[]
): ProjectOptionGroup<T>[] {
  const groups = new Map<string, ProjectOptionGroup<T>>()
  for (const team of teams) {
    const organization = team.organization ?? null
    const key = organization ? `org:${organization.pid}` : 'none'
    let group = groups.get(key)
    if (!group) {
      group = {
        key,
        name: organization?.name ?? 'Other projects',
        organization,
        via: [],
        memberOrganizationPid:
          organization && memberOrganizationPids.includes(organization.pid) ? organization.pid : null,
        projects: []
      }
      groups.set(key, group)
    }
    const via = team.access?.source === 'agency' ? team.access.via?.name : undefined
    if (via && !group.via.includes(via)) {
      group.via.push(via)
    }
    group.projects.push(team)
  }

  const ordered = [...groups.values()].sort(
    (a, b) => Number(b.memberOrganizationPid !== null) - Number(a.memberOrganizationPid !== null)
  )
  const [only] = ordered
  if (ordered.length === 1 && only && !only.organization) {
    only.name = null
  }
  return ordered
}
